class HeySmileProduct extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.price = this.querySelector("[data-hs-price]");
    this.button = this.querySelector("[data-hs-submit]");
    this.saveNote = this.querySelector("[data-hs-save-note]");
    this.stickyPrice = this.querySelector("[data-hs-sticky-price]");
    this.stickySave = this.querySelector("[data-hs-sticky-save]");
    this.stickyButton = this.querySelector("[data-hs-sticky-submit]");
    this.radios = [...this.querySelectorAll("[data-hs-variant]")];
    this.radios.forEach((radio) =>
      radio.addEventListener("change", () => this.select(radio)),
    );
    this.heroImage = this.querySelector(".hs-gallery__main img");
    this.thumbs = [...this.querySelectorAll("[data-hs-gallery-src]")];
    this.thumbs.forEach((thumb) =>
      thumb.addEventListener("click", () => this.showMedia(thumb)),
    );
    this.stickyButton?.addEventListener("click", () =>
      this.form?.requestSubmit(),
    );
    const checked = this.radios.find((radio) => radio.checked);
    if (checked) this.select(checked);
    this.initPincode();
    this.initCartFlow();
    this.preloadGallery();
    const buybox = this.querySelector(".hs-buybox");
    if (buybox && "IntersectionObserver" in window) {
      const io = new IntersectionObserver(([entry]) => {
        this.classList.toggle(
          "hs-sticky-visible",
          !entry.isIntersecting && entry.boundingClientRect.top < 0,
        );
      });
      io.observe(buybox);
    }
  }

  select(radio) {
    if (this.price) this.price.textContent = radio.dataset.price;
    if (this.stickyPrice) this.stickyPrice.textContent = radio.dataset.price;
    if (this.stickySave)
      this.stickySave.textContent = radio.dataset.saveShort || "";
    if (this.saveNote) this.saveNote.textContent = radio.dataset.saveNote || "";
    const label = this.button?.dataset.label || "Add to cart";
    this.button.disabled = radio.dataset.available !== "true";
    this.button.textContent = this.button.disabled
      ? "Sold out"
      : `${label} — ${radio.dataset.price}`;
    this.radios.forEach((item) =>
      item.closest(".hs-offer")?.classList.toggle("is-selected", item.checked),
    );
  }

  showMedia(thumb) {
    if (!this.heroImage) return;
    const src = thumb.dataset.hsGallerySrc;
    this.thumbs.forEach((item) =>
      item.classList.toggle("is-active", item === thumb),
    );
    const wrap = this.heroImage.closest(".hs2-gal-main");
    if (!wrap) {
      this.heroImage.src = src;
      this.heroImage.srcset = "";
      return;
    }
    wrap.classList.add("is-swapping");
    const pre = new Image();
    const apply = () => {
      this.heroImage.src = src;
      this.heroImage.srcset = "";
      requestAnimationFrame(() =>
        requestAnimationFrame(() => wrap.classList.remove("is-swapping")),
      );
    };
    pre.onload = apply;
    pre.onerror = apply;
    pre.src = src;
  }

  preloadGallery() {
    if (!this.thumbs.length) return;
    const warm = () =>
      this.thumbs.forEach((thumb) => {
        const src = thumb.dataset.hsGallerySrc;
        if (!src) return;
        const img = new Image();
        img.src = src;
      });
    if ("requestIdleCallback" in window)
      requestIdleCallback(warm, { timeout: 2000 });
    else setTimeout(warm, 600);
  }

  initCartFlow() {
    if (!this.form || !this.querySelector(".hs2-atc")) return;
    this._onPageShow = () => this.resetAtc();
    window.addEventListener("pageshow", this._onPageShow);
    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      if (!this.ajaxBusy) this.addToCart();
    });
  }

  async addToCart() {
    // Buy Now: skip the cart entirely and go straight to checkout
    // (becomes Razorpay Magic Checkout automatically once that app is enabled).
    this.ajaxBusy = true;
    if (this.button) {
      this.button.disabled = true;
      this.button.textContent = "Taking you to checkout…";
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 10000);
    try {
      await fetch("/cart/clear.js", { method: "POST" });
      const res = await fetch("/cart/add.js", {
        method: "POST",
        body: new FormData(this.form),
        headers: { Accept: "application/json" },
        signal: ctrl.signal,
      });
      clearTimeout(timer);
      if (!res.ok) throw new Error("add failed");
      await res.json();
    } catch (err) {
      clearTimeout(timer);
      this.ajaxBusy = false;
      this.resetAtc();
      this.form.submit();
      return;
    }
    // Route through the store's standard checkout button so Razorpay Magic
    // Checkout can intercept it; fall back to a plain checkout redirect.
    const checkoutBtn = document.querySelector('[name="checkout"]');
    if (checkoutBtn) checkoutBtn.click();
    else window.location.href = "/checkout";
  }

  resetAtc() {
    if (!this.button) return;
    const radio = this.radios.find((r) => r.checked);
    const label = this.button.dataset.label || "Add to cart";
    this.button.disabled = radio ? radio.dataset.available !== "true" : false;
    this.button.textContent = this.button.disabled
      ? "Sold out"
      : `${label} — ${radio ? radio.dataset.price : ""}`;
  }

  disconnectedCallback() {
    if (this._onPageShow)
      window.removeEventListener("pageshow", this._onPageShow);
  }

  initPincode() {
    const widget = this.querySelector("[data-hs-pin]");
    if (!widget) return;
    const input = widget.querySelector("[data-hs-pin-input]");
    const output = widget.querySelector("[data-hs-pin-result]");
    const metroPrefixes = [
      "110",
      "400",
      "560",
      "600",
      "500",
      "700",
      "411",
      "380",
      "122",
      "201",
    ];
    const check = () => {
      const pin = (input.value || "").trim();
      if (!/^[1-9][0-9]{5}$/.test(pin)) {
        output.textContent = "Please enter a valid 6-digit pincode.";
        return;
      }
      const isMetro = metroPrefixes.includes(pin.slice(0, 3));
      const days = isMetro
        ? widget.dataset.metroDays
        : widget.dataset.otherDays;
      output.textContent = `Estimated delivery to ${pin}: ${days}. COD available.`;
    };
    widget
      .querySelector("[data-hs-pin-check]")
      ?.addEventListener("click", check);
    input?.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        check();
      }
    });
  }
}

if (!customElements.get("hey-smile-product")) {
  customElements.define("hey-smile-product", HeySmileProduct);
}

document.addEventListener("click", (event) => {
  const filter = event.target.closest("[data-hs-faq-filter]");
  if (!filter) return;
  const section = filter.closest("[data-hs-faq]");
  if (!section) return;
  const category = filter.dataset.hsFaqFilter;
  section.querySelectorAll("[data-hs-faq-filter]").forEach((button) => {
    button.classList.toggle("is-active", button === filter);
  });
  section.querySelectorAll("[data-hs-faq-item]").forEach((item) => {
    item.hidden = category !== "all" && item.dataset.hsFaqItem !== category;
    if (item.hidden) item.open = false;
  });
});

(() => {
  const videos = document.querySelectorAll("[data-hs-video]");
  if (!videos.length) return;
  if (!("IntersectionObserver" in window)) {
    videos.forEach((video) => {
      video.preload = "metadata";
    });
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          if (video.readyState === 0) video.load();
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    },
    { threshold: 0.4 },
  );
  videos.forEach((video) => observer.observe(video));
})();

(() => {
  const nav = performance.getEntriesByType?.("navigation")?.[0];
  if (nav && nav.type === "reload" && !location.hash) {
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
    window.addEventListener("pageshow", () => window.scrollTo(0, 0), {
      once: true,
    });
  }
})();
